import React from 'react';
import { Link } from 'react-router-dom';
import { Hash, Trophy, Users, MessageCircle, Activity } from 'lucide-react';
import { createCategorySlug } from '../utils/urlHelpers';
import { getCategoryPeers, getForumRank, type RankField } from '../utils/forumRank';
import type { ForumBase } from '../types/forum';

interface Props {
  forum: ForumBase;
  allForums: ForumBase[];
  darkMode: boolean;
  className?: string;
}

const METRICS: { field: RankField; label: string; icon: React.ComponentType<{ size?: number; className?: string }> }[] = [
  { field: 'score', label: 'Skor', icon: Trophy },
  { field: 'members_count', label: 'Üye', icon: Users },
  { field: 'posts_count', label: 'İleti', icon: Activity },
  { field: 'topics_count', label: 'Konu', icon: MessageCircle },
];

const ForumCategoryRank: React.FC<Props> = ({ forum, allForums, darkMode, className = '' }) => {
  const categoryPeers = getCategoryPeers(allForums, forum.category);
  if (categoryPeers.length < 2) return null;

  const box = darkMode
    ? 'bg-gray-800/80 border-gray-700 text-gray-100'
    : 'bg-indigo-50/90 border-indigo-100 text-gray-900';

  return (
    <section
      className={`mt-8 rounded-xl border p-4 sm:p-6 ${box} ${className}`}
      aria-labelledby="forum-rank-heading"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2
            id="forum-rank-heading"
            className={`text-lg font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-indigo-900'}`}
          >
            <Hash className="text-indigo-500 shrink-0" size={22} />
            Sıralama — {forum.category}
          </h2>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Bu forumun kategorideki ve tüm listedeki konumu
          </p>
        </div>
        <Link
          to={`/category/${createCategorySlug(forum.category)}`}
          className={`text-sm font-medium shrink-0 ${
            darkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'
          }`}
        >
          Kategoriye git →
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {METRICS.map(({ field, label, icon: Icon }) => {
          const catRank = getForumRank(forum, categoryPeers, field);
          const globalRank = getForumRank(forum, allForums, field);
          return (
            <div
              key={field}
              className={`rounded-lg border p-3 ${
                darkMode ? 'bg-gray-900/50 border-gray-600' : 'bg-white border-indigo-100'
              }`}
            >
              <div className={`flex items-center gap-1.5 text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <Icon size={14} />
                {label}
              </div>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Kategori{' '}
                <span className={`font-bold tabular-nums ${darkMode ? 'text-amber-300' : 'text-indigo-700'}`}>
                  #{catRank}
                </span>
              </p>
              <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Genel{' '}
                <span className={`font-bold tabular-nums ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>
                  #{globalRank}
                </span>
              </p>
            </div>
          );
        })}
      </div>

      <p className={`mt-4 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
        <Link to="/leaderboard" className={darkMode ? 'text-indigo-400 hover:underline' : 'text-indigo-600 hover:underline'}>
          Forum lig tablosu
        </Link>
        {' '}· {categoryPeers.length} forum bu kategoride listeleniyor
      </p>
    </section>
  );
};

export default ForumCategoryRank;
